import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Button, Confirm, Message, Segment, Table } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import config from '@plone/volto/registry';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { useClient } from '@plone/volto/hooks/client/useClient';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Error from '@plone/volto/components/theme/Error/Error';
import Forbidden from '@plone/volto/components/theme/Forbidden/Forbidden';
import Unauthorized from '@plone/volto/components/theme/Unauthorized/Unauthorized';
import backSVG from '@plone/volto/icons/back.svg';

import {
  createMpfPerfil,
  deleteMpfPerfil,
  getMpfPerfisCatalogo,
  getMpfPermissoes,
  listMpfPerfis,
  updateMpfPerfil,
} from 'volto-ministerio-publico-federal/actions';
import { useMpfPermissoes } from 'volto-ministerio-publico-federal/hooks';
import {
  mensagemDeErro,
  type MpfPerfil,
} from 'volto-ministerio-publico-federal/helpers/Permissoes';
import { catalogoBlocos } from './catalogoBlocos';
import PerfilEditor, { type Catalogo } from './PerfilEditor';

const messages = defineMessages({
  back: { id: 'Back', defaultMessage: 'Voltar' },
  title: { id: 'Access profiles', defaultMessage: 'Perfis de acesso' },
  saved: { id: 'Access profile saved', defaultMessage: 'Perfil salvo.' },
  deleted: { id: 'Access profile deleted', defaultMessage: 'Perfil excluído.' },
  confirmDelete: {
    id: 'Access profile confirm delete',
    defaultMessage:
      'Excluir o perfil “{title}”? Quem o recebeu perde na hora o que ele liberava.',
  },
  delete: { id: 'Delete', defaultMessage: 'Excluir' },
  cancel: { id: 'Cancel', defaultMessage: 'Cancelar' },
});

const PermissoesControlpanel = () => {
  const intl = useIntl();
  const dispatch = useDispatch<any>();
  const { pathname } = useLocation();
  const isClient = useClient();
  const token = useSelector((state: any) => state.userSession?.token);
  const permissoes = useMpfPermissoes();
  const perfis = useSelector((state: any) => state.mpfPerfis);
  const [editando, setEditando] = useState<MpfPerfil | 'novo' | null>(null);
  const [excluindo, setExcluindo] = useState<MpfPerfil | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogo: Catalogo | null = perfis?.catalogo ?? null;
  const blocos = useMemo(
    () =>
      catalogoBlocos(
        config.blocks?.blocksConfig,
        catalogo?.blocks_obrigatorios ?? [],
      ),
    [catalogo],
  );

  useEffect(() => {
    dispatch(listMpfPerfis());
    dispatch(getMpfPerfisCatalogo());
  }, [dispatch]);

  const erroLista = perfis?.list?.error ?? perfis?.catalogoRequest?.error;
  const statusErro = erroLista?.status ?? erroLista?.response?.status;
  if (
    (permissoes && !permissoes.is_manager) ||
    [401, 403].includes(statusErro)
  ) {
    return token ? <Forbidden /> : <Unauthorized />;
  }
  if (erroLista) {
    return <Error error={erroLista} />;
  }

  const recarregar = () => {
    dispatch(listMpfPerfis());
    // O próprio gestor pode ter se incluído num perfil.
    dispatch(getMpfPermissoes());
  };

  const salvar = async (dados: Record<string, unknown>) => {
    try {
      if (editando === 'novo') {
        await dispatch(createMpfPerfil(dados));
      } else if (editando) {
        await dispatch(updateMpfPerfil(editando.id, dados));
      }
      setEditando(null);
      setAviso(intl.formatMessage(messages.saved));
      recarregar();
    } catch {
      // A mensagem do backend fica no reducer (create/update.error).
    }
  };

  const excluir = async () => {
    if (!excluindo) return;
    try {
      await dispatch(deleteMpfPerfil(excluindo.id));
      setAviso(intl.formatMessage(messages.deleted));
      recarregar();
    } catch {
      // Idem: erro exibido a partir do reducer.
    } finally {
      setExcluindo(null);
    }
  };

  const pedidoEscrita = editando === 'novo' ? perfis?.create : perfis?.update;
  const itens: MpfPerfil[] = perfis?.items ?? [];

  return (
    <div id="mpf-permissoes-page" className="mpf-permissoes-page__container">
      <Helmet title={intl.formatMessage(messages.title)} />
      <Segment.Group raised>
        <Segment className="primary">
          {intl.formatMessage(messages.title)}
        </Segment>
        <Segment>
          <p>
            <FormattedMessage
              id="Access profiles intro"
              defaultMessage="Um perfil libera painéis de controle e permissões e pode restringir menus, layouts, tipos e blocos. É atribuído a grupos ou usuários e nunca dá acesso de administrador (Manager)."
            />
          </p>
          {aviso && (
            <Message
              positive
              onDismiss={() => setAviso(null)}
              content={aviso}
            />
          )}
          {perfis?.delete?.error && (
            <Message negative content={mensagemDeErro(perfis.delete.error)} />
          )}

          {editando && catalogo ? (
            <PerfilEditor
              key={editando === 'novo' ? 'novo' : editando.id}
              perfil={editando === 'novo' ? null : editando}
              catalogo={catalogo}
              blocos={blocos}
              gruposDeBlocos={config.blocks?.groupBlocksOrder}
              nomesDeLayout={config.views?.layoutViewsNamesMapping}
              salvando={Boolean(pedidoEscrita?.loading)}
              erro={mensagemDeErro(pedidoEscrita?.error)}
              onSalvar={salvar}
              onCancelar={() => setEditando(null)}
            />
          ) : (
            <>
              <Button
                primary
                disabled={!catalogo}
                onClick={() => {
                  setAviso(null);
                  setEditando('novo');
                }}
              >
                <FormattedMessage
                  id="Add access profile"
                  defaultMessage="Novo perfil"
                />
              </Button>
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>
                      <FormattedMessage
                        id="Access profile title"
                        defaultMessage="Nome do perfil"
                      />
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                      <FormattedMessage
                        id="Access profile principals"
                        defaultMessage="Grupos e usuários com este perfil"
                      />
                    </Table.HeaderCell>
                    <Table.HeaderCell />
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {itens.length === 0 && perfis?.list?.loaded && (
                    <Table.Row>
                      <Table.Cell colSpan={3}>
                        <FormattedMessage
                          id="No access profiles"
                          defaultMessage="Nenhum perfil criado ainda."
                        />
                      </Table.Cell>
                    </Table.Row>
                  )}
                  {itens.map((perfil) => (
                    <Table.Row key={perfil.id}>
                      <Table.Cell>
                        <strong>{perfil.title}</strong>
                        {perfil.description && <div>{perfil.description}</div>}
                      </Table.Cell>
                      <Table.Cell>
                        {perfil.principals.map((p) => p.title).join(', ') ||
                          '—'}
                      </Table.Cell>
                      <Table.Cell collapsing>
                        <Button
                          size="small"
                          disabled={!catalogo}
                          onClick={() => {
                            setAviso(null);
                            setEditando(perfil);
                          }}
                        >
                          <FormattedMessage id="Edit" defaultMessage="Editar" />
                        </Button>
                        <Button
                          size="small"
                          negative
                          basic
                          onClick={() => setExcluindo(perfil)}
                        >
                          {intl.formatMessage(messages.delete)}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          )}
        </Segment>
      </Segment.Group>

      <Confirm
        open={Boolean(excluindo)}
        content={intl.formatMessage(messages.confirmDelete, {
          title: excluindo?.title ?? '',
        })}
        confirmButton={intl.formatMessage(messages.delete)}
        cancelButton={intl.formatMessage(messages.cancel)}
        onCancel={() => setExcluindo(null)}
        onConfirm={excluir}
      />

      {isClient &&
        createPortal(
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <UniversalLink href="/controlpanel" className="item">
                <Icon
                  name={backSVG}
                  aria-label={intl.formatMessage(messages.back)}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </UniversalLink>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </div>
  );
};

export default PermissoesControlpanel;
